import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import {
  MapPin, ArrowSquareOut, Calendar, LinkSimple, VideoCamera, MicrophoneStage,
  FileText, Camera, Article, Sparkle, ArrowLeft, PencilSimple, EyeSlash,
} from "@phosphor-icons/react";
import ShareButton from "@/components/ShareButton";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import MarkdownBody from "@/components/MarkdownBody";
import { useAuth } from "@/contexts/AuthContext";
import EditArticleModal from "./EditArticleModal";
import { VideoShowcase, useStoryVideos } from "@/components/VideoShowcase";

const LINK_ICON = {
  webpage: LinkSimple, news: LinkSimple, research: Article,
  government: FileText, video: VideoCamera, podcast: MicrophoneStage,
  scientific_publication: Article, educational: Article, other: LinkSimple,
};

const STATUS_COLOR = {
  active: "text-emerald-700 bg-emerald-50 border-emerald-200",
  redirected: "text-amber-800 bg-amber-50 border-amber-200",
  paywalled: "text-sky-700 bg-sky-50 border-sky-200",
  "temp-unavailable": "text-amber-700 bg-amber-50 border-amber-200",
  broken: "text-rose-700 bg-rose-50 border-rose-200",
  closed: "text-stone-700 bg-stone-100 border-stone-300",
};

function StatusChip({ status }) {
  if (!status) return null;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-mono-tech tracking-[0.2em] uppercase border ${STATUS_COLOR[status] || "text-stone-700 bg-stone-50 border-stone-200"}`}>
      {status}
    </span>
  );
}

/**
 * Story detail — renders the article body via the shared MarkdownBody
 * component (see components/MarkdownBody.jsx) which supports headings,
 * bullets, inline links, aligned figures (left/center/right), auto-galleries,
 * and lite-loading YouTube / Vimeo / Loom embeds.
 */

export default function StoryDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [related, setRelated] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [translatedBody, setTranslatedBody] = useState(null);
  const isAdmin = !!user?.is_admin;

  const reload = async () => {
    try {
      const { data } = await http.get(`/nature/stories/${id}`);
      setStory(data.news || data.story || null);
      setRelated(data.related_observations || []);
      setSiblings(data.siblings || []);
    } catch { /* 404 handled below */ }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get(`/nature/stories/${id}`);
        if (!alive) return;
        setStory(data.news || data.story || null);
        setRelated(data.related_observations || []);
        setSiblings(data.siblings || []);
      } catch { /* 404 handled below */ } finally { setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  // NOTE: this hook MUST be called on every render (before any conditional
  // return) to satisfy the rules-of-hooks lint. The hook itself handles a
  // null story internally.
  const videos = useStoryVideos(story);

  if (loading) {
    return <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10">
      <div className="max-w-[900px] mx-auto animate-pulse space-y-4">
        <div className="h-8 w-1/3 bg-white/60 rounded" />
        <div className="h-14 w-3/4 bg-white/60 rounded" />
        <div className="h-64 bg-white/60 rounded-2xl" />
      </div>
    </main>;
  }
  if (!story) {
    return <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10">
      <div className="max-w-[900px] mx-auto">
        <div className="font-serif-display text-3xl">Story not found.</div>
        <Link to="/stories" className="mt-2 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          <ArrowLeft size={12} weight="bold" /> Back to stories
        </Link>
      </div>
    </main>;
  }

  const cover = story.media?.find((m) => m.media_type === "image")?.url;
  const loc = [story.location?.city || story.location?.village, story.location?.state, story.location?.country].filter(Boolean).join(", ");
  const links = story.links || [];
  const otherMedia = (story.media || []).filter((m) => m.media_type !== "image" && m.media_type !== "video");
  const hideAttribution = !!story.hide_source_attribution;
  const bodyLength = story.body?.length || 0;
  const longRead = bodyLength > 800;
  // If attribution is hidden, strip the "This NaturePulse article was inspired by…" opener from the body.
  const displayBody = hideAttribution && story.body
    ? story.body.replace(/^\s*This NaturePulse article[^\n]*\n+/i, "").replace(/^\s*##\s*Original Source[\s\S]*?(?=\n##\s|$)/i, "")
    : story.body;

  return (
    <main className="bg-[#F5EBDC] text-[#151312] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="story-detail">
      <div className="max-w-[900px] mx-auto">
        <div className="flex items-center justify-between gap-2">
          <Link to="/stories" className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <ArrowLeft size={12} weight="bold" /> All stories
          </Link>
          {isAdmin && (
            <button
              onClick={() => setEditing(true)}
              data-testid="story-admin-edit"
              className="inline-flex items-center gap-1 rounded-full border border-[color:var(--np-brand,#B85F2A)] bg-[color:var(--np-brand,#B85F2A)] text-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:opacity-90"
            >
              <PencilSimple size={12} weight="fill" /> Edit article
            </button>
          )}
        </div>

        <div className="mt-4 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] flex items-center gap-2 flex-wrap">
          <span>{story.story_type?.replace(/_/g, " ")} · {story.category}</span>
          {hideAttribution && (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-[#151312] text-[#E4A15C] text-[9px]">
              Editorial article
            </span>
          )}
          {isAdmin && hideAttribution && (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-amber-50 border border-amber-300 text-amber-800 text-[9px] normal-case tracking-wider">
              <EyeSlash size={10} weight="fill" /> Attribution hidden
            </span>
          )}
        </div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight" data-testid="story-title">
          {story.title || story.headline}
        </h1>

        <div className="mt-3 flex flex-wrap gap-3 text-[12px] text-[#3A3830] font-mono-tech tracking-wide">
          {loc && <span className="inline-flex items-center gap-1"><MapPin size={11} weight="fill" /> {loc}</span>}
          {(story.event_date || story.publication_date) && (
            <span className="inline-flex items-center gap-1"><Calendar size={11} weight="fill" />
              {new Date(story.event_date || story.publication_date).toISOString().slice(0, 10)}
            </span>
          )}
          {story.publisher && <span>· {story.publisher}</span>}
          <StatusChip status={story.link_status} />
          <div className="ml-auto"><ShareButton title={story.title || story.headline} text={story.summary || ""} shortKind="story" shortId={story.id} variant="icon" testid="story-share" /></div>
        </div>

        {cover && (
          <figure className="mt-6 rounded-2xl overflow-hidden bg-black/5 border border-black/10">
            <img src={cover} alt="" className="w-full max-h-[540px] object-cover" data-testid="story-cover" />
          </figure>
        )}

        {/* Multiple videos: 2-across grid ABOVE the body, always */}
        {videos.length >= 2 && (
          <VideoShowcase videos={videos} bodyLength={0} />
        )}

        {/* Single video + short body: full-width above body */}
        {videos.length === 1 && !longRead && (
          <VideoShowcase videos={videos} bodyLength={0} />
        )}

        {story.body && (
          <article className="mt-6 rounded-2xl bg-white border border-black/10 p-6 md:p-8" data-testid="story-body">
            {/* Listen + Translate for this article */}
            <div className="mb-5 pb-4 border-b border-black/10">
              <ListenTranslateBar
                text={`${story.title || ""}\n\n${displayBody || ""}`}
                onTranslated={setTranslatedBody}
                testidPrefix="story-tools"
              />
            </div>
            {/* Single video + long read: float right, text wraps around */}
            {videos.length === 1 && longRead && (
              <VideoShowcase videos={videos} bodyLength={bodyLength} className="not-prose" />
            )}
            {translatedBody ? (
              <div className="font-serif-display text-[16px] md:text-[17px] leading-relaxed text-[#241209] whitespace-pre-wrap" data-testid="story-body-translated">
                {translatedBody}
              </div>
            ) : (
              <MarkdownBody text={displayBody} />
            )}
            <div className="clear-both" />
            {story.composed_by_ai && !hideAttribution && (
              <div className="mt-4 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                <Sparkle size={11} weight="fill" /> AI-composed article — always verify with the original source
              </div>
            )}
          </article>
        )}

        {story.summary && !story.body && (
          <p className="mt-6 font-serif-display text-lg md:text-xl leading-[1.7] text-[#241209]">
            {story.summary}
          </p>
        )}

        {/* Photos gallery (excluding the cover) */}
        {story.media?.filter((m) => m.media_type === "image" && m.url !== cover).length > 0 && (
          <div className="mt-6">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">/ Photos</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="story-gallery">
              {story.media.filter((m) => m.media_type === "image" && m.url !== cover).map((m, i) => (
                <img key={i} src={m.url} alt="" className="w-full aspect-[4/3] object-cover rounded-lg border border-black/10" />
              ))}
            </div>
          </div>
        )}

        {/* Non-image media */}
        {otherMedia.length > 0 && (
          <div className="mt-6 space-y-2" data-testid="story-media">
            {otherMedia.map((m, i) => {
              const Icon = m.media_type === "video" ? VideoCamera : m.media_type === "audio" ? MicrophoneStage : FileText;
              return (
                <a key={i} href={m.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-lg border border-black/10 bg-white/70 px-3 py-2 hover:bg-white">
                  <Icon size={18} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
                  <div className="flex-1 min-w-0 text-sm truncate">{m.caption || m.url}</div>
                  <ArrowSquareOut size={12} weight="bold" />
                </a>
              );
            })}
          </div>
        )}

        {/* External links */}
        {links.length > 0 && (
          <div className="mt-6" data-testid="story-links">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">/ Sources & useful links</div>
            <ul className="space-y-2">
              {links.map((L, i) => {
                const Icon = LINK_ICON[L.link_type] || LinkSimple;
                return (
                  <li key={i} className="rounded-lg border border-black/10 bg-white/70 p-3">
                    <a href={L.url} target="_blank" rel="noreferrer" className="flex items-start gap-3 group">
                      <Icon size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)] mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#151312] group-hover:underline break-all">{L.url}</div>
                        <div className="flex gap-2 items-center text-[11px] text-[#3A3830] mt-0.5">
                          <span>{L.source_domain || L.link_type}</span>
                          <StatusChip status={L.link_status} />
                        </div>
                        {L.user_note && <div className="mt-1 text-xs text-[#3A3830] italic">"{L.user_note}"</div>}
                      </div>
                      <ArrowSquareOut size={12} weight="bold" className="mt-1 opacity-60" />
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Primary source card (news_link legacy) */}
        {story.story_type === "news_link" && story.canonical_url && !links.length && (
          <a href={story.canonical_url} target="_blank" rel="noreferrer" className="mt-6 block rounded-2xl border border-black/10 bg-white/70 p-4 hover:bg-white" data-testid="story-source">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">/ Primary source</div>
            <div className="mt-1 font-serif-display text-lg">{story.publisher}</div>
            <div className="text-xs text-[#3A3830] break-all">{story.canonical_url}</div>
            <div className="mt-2 flex items-center gap-2"><StatusChip status={story.link_status} /></div>
          </a>
        )}

        {/* Source-to-Story: rich source attribution */}
        {!hideAttribution && story.sources?.length > 0 && (
          <div className="mt-8" data-testid="story-sources">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
              / {story.sources.length === 1 ? "Original source" : "Original sources"}
            </div>
            <div className="space-y-3">
              {story.sources.map((s, i) => (
                <a
                  key={i}
                  href={s.canonical_url || s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex gap-3 rounded-2xl border border-black/10 bg-white/70 p-3 hover:bg-white"
                  data-testid={`story-source-${i}`}
                >
                  {s.thumbnail && (
                    <img src={s.thumbnail} alt="" className="w-24 h-24 object-cover rounded-lg border border-black/10 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                      {s.platform || "web"} · {s.source_type || "article"}
                    </div>
                    <div className="font-serif-display text-base mt-0.5 line-clamp-2">{s.title || s.url}</div>
                    <div className="text-xs text-[#3A3830] mt-0.5">
                      {s.creator || s.publisher || "Unknown creator"}
                    </div>
                    <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[color:var(--np-brand,#B85F2A)]">
                      <ArrowSquareOut size={11} weight="bold" /> Watch / read the original
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Source-to-Story: verified claims */}
        {!hideAttribution && story.claims?.length > 0 && (
          <div className="mt-8" data-testid="story-claims">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
              / Claims verification
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {story.claims.map((c, i) => {
                const st = (c.verification_status || "unable_to_verify").toLowerCase();
                const styleMap = {
                  verified: "bg-emerald-50 text-emerald-800 border-emerald-300",
                  generally_supported: "bg-teal-50 text-teal-800 border-teal-300",
                  partially_correct: "bg-amber-50 text-amber-800 border-amber-300",
                  disputed: "bg-orange-50 text-orange-800 border-orange-300",
                  misleading: "bg-rose-50 text-rose-800 border-rose-300",
                  false: "bg-rose-100 text-rose-900 border-rose-400",
                  unable_to_verify: "bg-stone-100 text-stone-700 border-stone-300",
                };
                return (
                  <div key={i} className={`rounded-lg border p-2 text-xs leading-snug ${styleMap[st] || styleMap.unable_to_verify}`}>
                    <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase opacity-80 mb-1">
                      {st.replace(/_/g, " ")}
                      {c.confidence != null && <> · conf {Number(c.confidence).toFixed(2)}</>}
                    </div>
                    <div>{c.text || c.claim}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Source-to-Story: additional references */}
        {story.references?.length > 0 && (
          <div className="mt-8" data-testid="story-references">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
              / Independent references
            </div>
            <ul className="space-y-1.5">
              {story.references.map((r, i) => (
                <li key={i} className="rounded-lg border border-black/10 bg-white/70 p-2.5">
                  <a href={r.url} target="_blank" rel="noreferrer" className="flex items-start gap-2 group">
                    <Article size={14} weight="fill" className="text-[color:var(--np-brand,#B85F2A)] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#151312] group-hover:underline line-clamp-2">{r.title}</div>
                      <div className="text-[11px] text-[#3A3830] mt-0.5">
                        {r.organization}
                        {r.credibility_level && <span> · {r.credibility_level} credibility</span>}
                        {r.reference_type && <span> · {r.reference_type}</span>}
                      </div>
                    </div>
                    <ArrowSquareOut size={11} weight="bold" className="mt-1 opacity-60" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Related citizen observations */}
        {related.length > 0 && (
          <div className="mt-8" data-testid="story-related-observations">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
              / Related NaturePulse observations
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link key={r.id} to={`/observations/${r.id}`} className="flex gap-3 rounded-lg border border-black/10 bg-white/70 hover:bg-white p-3">
                  {r.media?.[0]?.url ? (
                    <img src={r.media[0].url} alt="" className="w-16 h-16 object-cover rounded" />
                  ) : (
                    <div className="w-16 h-16 rounded bg-[#FBF2E1] grid place-items-center">
                      <Camera size={18} className="text-[color:var(--np-brand,#B85F2A)]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-serif-display leading-tight line-clamp-2">{r.title}</div>
                    <div className="text-[11px] text-[#3A3830] mt-0.5">
                      {[r.location?.city, r.location?.state, r.location?.country].filter(Boolean).join(", ")}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {siblings.length > 0 && (
          <div className="mt-8" data-testid="story-siblings">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
              / More sources on this event
            </div>
            <ul className="space-y-2">
              {siblings.map((s) => (
                <li key={s.id}>
                  <Link to={`/stories/${s.id}`} className="flex items-baseline gap-2 rounded-lg border border-black/10 bg-white/70 p-3 hover:bg-white">
                    <span className="font-serif-display text-sm flex-1">{s.headline}</span>
                    <span className="text-[11px] text-[#3A3830]">{s.publisher}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Categories & tag chips — small, 4-across grid, mobile-safe.
            Clicking any chip filters /news by that category. Every chip
            passes the ScrollToTop route change so readers land at the top
            of the filtered feed instead of somewhere mid-page. */}
        {(story.category || (story.tags && story.tags.length > 0)) && (
          <div className="mt-10" data-testid="story-topic-chips">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
              / Explore more in these topics
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {story.category && (
                <Link
                  to={`/news?category=${encodeURIComponent(story.category)}`}
                  data-testid={`story-chip-category-${story.category}`}
                  className="rounded-full border border-[color:var(--np-brand,#B85F2A)] bg-[color:var(--np-brand,#B85F2A)] text-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-center truncate hover:opacity-90"
                >
                  {story.category}
                </Link>
              )}
              {(story.tags || []).slice(0, 15).map((t) => (
                <Link
                  key={t}
                  to={`/news?category=${encodeURIComponent(t)}`}
                  data-testid={`story-chip-tag-${t}`}
                  className="rounded-full border border-black/15 bg-white/80 hover:bg-white hover:border-[color:var(--np-brand,#B85F2A)] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#241209] text-center truncate transition-colors"
                >
                  {t}
                </Link>
              ))}
              {story.species && (
                <Link
                  to={`/observations?species=${encodeURIComponent(story.species)}`}
                  data-testid={`story-chip-species`}
                  className="rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-center truncate hover:bg-emerald-100"
                >
                  {story.species}
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Share, short-URL + QR block (always visible) */}
        <div className="mt-10 rounded-2xl border border-black/10 bg-white/70 p-5" data-testid="story-share-block">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
            / Share this article
          </div>
          <div className="text-sm text-[#3A3830] mb-3">
            Copy a branded short link, download the QR code, or send it out on your favourite channels.
          </div>
          <ShareButton
            title={story.title || story.headline}
            text={story.summary || story.one_sentence_summary || ""}
            shortKind="story"
            shortId={story.id}
            preShortUrl={story.short_url}
            preQrUrl={story.qr_url}
            variant="pill"
            size="md"
            testid="story-share-full"
          />
        </div>
      </div>

      {editing && (
        <EditArticleModal
          articleId={story.id}
          onClose={() => setEditing(false)}
          onSaved={reload}
        />
      )}
    </main>
  );
}
